// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, type RefObject, useCallback, useImperativeHandle, useMemo, useRef } from 'react';

import {
  useEvolveDelete,
  useEvolvePatch,
  type FacilityId,
  type UpdateWorkOrderBody,
  type WorkOrder,
  type WorkOrderId,
  useEvolvePost,
  type BlockWorkOrderBody,
  extractErrorMessage,
  isBadRequest,
  isCanceledApiRequest,
} from '@evolvemep/api-ts';

import { useMantineNotifications } from 'components/Mantine/useMantineNotifications';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { WorkOrderModalProvider, useWorkOrderModals } from 'modules/Shop/WorkOrders/WorkOrdersPage/modalRegistries';

type WorkOrdersContextType = {
  setWorkOrderAsPending: (workOrderId: WorkOrderId) => Promise<void>;
  submitWorkOrder: (workOrderId: WorkOrderId) => Promise<void>;
  approveWorkOrder: (workOrderId: WorkOrderId) => Promise<void>;
  releaseWorkOrder: (workOrderId: WorkOrderId) => Promise<void>;
  rejectWorkOrder: (workOrderId: WorkOrderId, reason: string) => Promise<void>;
  cancelWorkOrder: (workOrderId: WorkOrderId, reason: string) => Promise<void>;
  denyCancelRequest: (workOrderId: WorkOrderId, reason: string) => Promise<void>;
  acceptCancelRequest: (workOrderId: WorkOrderId, taskActions: BlockWorkOrderBody['taskActions']) => Promise<void>;
  blockWorkOrder: (workOrderId: WorkOrderId, taskActions: BlockWorkOrderBody['taskActions']) => Promise<void>;
  transferWorkOrder: (workOrderId: WorkOrderId, reason: string, facilityId: FacilityId) => Promise<void>;
  confirmTransfer: (workOrderId: WorkOrderId) => Promise<void>;
  updateWorkOrder: (workOrderId: WorkOrderId, body: UpdateWorkOrderBody) => Promise<WorkOrder>;
  deleteWorkOrder: (workOrderId: WorkOrderId) => Promise<void>;
  mutationRunning: boolean;
};

const WorkOrdersContext = React.createContext<WorkOrdersContextType | undefined>(undefined);

type OpenActionBlockedModal = (title: string, message: string) => void;

/**
 * The modal registry renders its modals beside its children, and those modals need `WorkOrdersContext`, so the
 * registry must sit inside the provider. This hands the registry's `openModal` back up to the provider.
 */
const ActionBlockedModalBridge = ({ openRef }: { openRef: RefObject<OpenActionBlockedModal | null> }) => {
  const { openModal } = useWorkOrderModals();
  useImperativeHandle(
    openRef,
    () => (title, message) => {
      openModal('workOrderActionBlockedModal', { title, message });
    },
    [openModal],
  );
  return null;
};

export const WorkOrdersProvider = ({ children }: { children: ReactNode }) => {
  const { showError } = useMantineNotifications();
  const openActionBlockedModalRef = useRef<OpenActionBlockedModal>(null);

  /** Shows a 400 as a modal titled `title` and any other failure as the usual toast, then rethrows so the caller stops. */
  const surfaceErrors = useCallback(
    async (title: string, request: Promise<unknown>) => {
      try {
        await request;
      } catch (error) {
        if (isBadRequest(error)) {
          openActionBlockedModalRef.current?.(title, extractErrorMessage(error));
        } else if (!isCanceledApiRequest(error)) {
          showError(error);
        }
        throw error;
      }
    },
    [showError],
  );

  // Mutations
  const { apiCall: submitApiCall, loading: submitting } = useEvolvePost('shop/workOrder/:workOrderId/submit', {
    alertOnError: false,
  });
  const { apiCall: setAsPendingApiCall, loading: settingAsPending } = useEvolvePost(
    'shop/workOrder/:workOrderId/set-as-pending',
  );
  const { apiCall: approveApiCall, loading: approving } = useEvolvePost('shop/workOrder/:workOrderId/approve', {
    alertOnError: false,
  });
  const { apiCall: releaseApiCall, loading: releasing } = useEvolvePost('shop/workOrder/:workOrderId/release', {
    alertOnError: false,
  });
  const { apiCall: rejectApiCall, loading: rejecting } = useEvolvePost('shop/workOrder/:workOrderId/reject', {
    alertOnError: false,
  });
  const { apiCall: cancelApiCall, loading: cancelling } = useEvolvePost('shop/workOrder/:workOrderId/cancel/request', {
    alertOnError: false,
  });
  const { apiCall: denyCancellationApiCall, loading: denyingCancellation } = useEvolvePost(
    'shop/workOrder/:workOrderId/cancel/deny',
    { alertOnError: false },
  );
  const { apiCall: acceptCancellationApiCall, loading: acceptingCancellation } = useEvolvePost(
    'shop/workOrder/:workOrderId/cancel/accept',
    { alertOnError: false },
  );
  const { apiCall: blockWorkOrderApiCall, loading: blocking } = useEvolvePost('shop/workOrder/:workOrderId/block', {
    alertOnError: false,
  });
  const { apiCall: transferWorkOrderStatusApiCall, loading: transferring } = useEvolvePost(
    'shop/workOrder/:workOrderId/transfer',
    { alertOnError: false },
  );
  const { apiCall: confirmTransferApiCall, loading: confirmingTransfer } = useEvolvePost(
    'shop/workOrder/:workOrderId/transfer/confirm',
    { alertOnError: false },
  );
  const { apiCall: updateWorkOrderApiCall, loading: saving } = useEvolvePatch('shop/workOrder/:workOrderId');
  const { apiCall: deleteWorkOrderApiCall, loading: deleting } = useEvolveDelete('shop/workOrder/:workOrderId', {
    alertOnError: false,
  });

  const submitWorkOrder = useCallback<WorkOrdersContextType['submitWorkOrder']>(
    async (workOrderId) => {
      await surfaceErrors(
        'Cannot submit work order',
        submitApiCall({}, { url: `shop/workOrder/${workOrderId}/submit` }),
      );
    },
    [submitApiCall, surfaceErrors],
  );
  const setWorkOrderAsPending = useCallback<WorkOrdersContextType['setWorkOrderAsPending']>(
    async (workOrderId) => {
      await setAsPendingApiCall({}, { url: `shop/workOrder/${workOrderId}/set-as-pending` });
    },
    [setAsPendingApiCall],
  );
  const approveWorkOrder = useCallback<WorkOrdersContextType['approveWorkOrder']>(
    async (workOrderId) => {
      await surfaceErrors(
        'Cannot approve work order',
        approveApiCall({}, { url: `shop/workOrder/${workOrderId}/approve` }),
      );
    },
    [approveApiCall, surfaceErrors],
  );
  const releaseWorkOrder = useCallback<WorkOrdersContextType['releaseWorkOrder']>(
    async (workOrderId) => {
      await surfaceErrors(
        'Cannot release work order',
        releaseApiCall({}, { url: `shop/workOrder/${workOrderId}/release` }),
      );
    },
    [releaseApiCall, surfaceErrors],
  );
  const rejectWorkOrder = useCallback<WorkOrdersContextType['rejectWorkOrder']>(
    async (workOrderId, reason) => {
      await surfaceErrors(
        'Cannot reject work order',
        rejectApiCall({ reason }, { url: `shop/workOrder/${workOrderId}/reject` }),
      );
    },
    [rejectApiCall, surfaceErrors],
  );
  const cancelWorkOrder = useCallback<WorkOrdersContextType['cancelWorkOrder']>(
    async (workOrderId, reason) => {
      await surfaceErrors(
        'Cannot cancel work order',
        cancelApiCall({ reason }, { url: `shop/workOrder/${workOrderId}/cancel/request` }),
      );
    },
    [cancelApiCall, surfaceErrors],
  );
  const denyCancelRequest = useCallback<WorkOrdersContextType['denyCancelRequest']>(
    async (workOrderId, reason) => {
      await surfaceErrors(
        'Cannot deny cancellation request',
        denyCancellationApiCall({ reason }, { url: `shop/workOrder/${workOrderId}/cancel/deny` }),
      );
    },
    [denyCancellationApiCall, surfaceErrors],
  );
  const acceptCancelRequest = useCallback<WorkOrdersContextType['acceptCancelRequest']>(
    async (workOrderId, taskActions) => {
      await surfaceErrors(
        'Cannot accept cancellation request',
        acceptCancellationApiCall({ taskActions }, { url: `shop/workOrder/${workOrderId}/cancel/accept` }),
      );
    },
    [acceptCancellationApiCall, surfaceErrors],
  );
  const blockWorkOrder = useCallback<WorkOrdersContextType['blockWorkOrder']>(
    async (workOrderId, taskActions) => {
      await surfaceErrors(
        'Cannot block work order',
        blockWorkOrderApiCall({ taskActions }, { url: `shop/workOrder/${workOrderId}/block` }),
      );
    },
    [blockWorkOrderApiCall, surfaceErrors],
  );
  const transferWorkOrder = useCallback<WorkOrdersContextType['transferWorkOrder']>(
    async (workOrderId, reason, facilityId) => {
      await surfaceErrors(
        'Cannot transfer work order',
        transferWorkOrderStatusApiCall(
          { reason },
          { url: `shop/workOrder/${workOrderId}/transfer`, params: { to: facilityId } },
        ),
      );
    },
    [transferWorkOrderStatusApiCall, surfaceErrors],
  );
  const confirmTransfer = useCallback<WorkOrdersContextType['confirmTransfer']>(
    async (workOrderId) => {
      await surfaceErrors(
        'Cannot confirm work order transfer',
        confirmTransferApiCall({}, { url: `shop/workOrder/${workOrderId}/transfer/confirm` }),
      );
    },
    [confirmTransferApiCall, surfaceErrors],
  );

  const updateWorkOrder = useCallback<WorkOrdersContextType['updateWorkOrder']>(
    async (workOrderId, body) => updateWorkOrderApiCall(body, { url: `shop/workOrder/${workOrderId}` }),
    [updateWorkOrderApiCall],
  );
  const deleteWorkOrder = useCallback<WorkOrdersContextType['deleteWorkOrder']>(
    async (workOrderId: WorkOrder['workOrderId']) => {
      await surfaceErrors('Cannot delete work order', deleteWorkOrderApiCall({ url: `shop/workOrder/${workOrderId}` }));
    },
    [deleteWorkOrderApiCall, surfaceErrors],
  );

  const value = useMemo<WorkOrdersContextType>(
    () => ({
      submitWorkOrder,
      setWorkOrderAsPending,
      approveWorkOrder,
      releaseWorkOrder,
      rejectWorkOrder,
      cancelWorkOrder,
      denyCancelRequest,
      acceptCancelRequest,
      blockWorkOrder,
      transferWorkOrder,
      confirmTransfer,
      updateWorkOrder,
      deleteWorkOrder,
      mutationRunning:
        saving ||
        submitting ||
        settingAsPending ||
        approving ||
        releasing ||
        rejecting ||
        cancelling ||
        denyingCancellation ||
        acceptingCancellation ||
        blocking ||
        transferring ||
        confirmingTransfer ||
        deleting,
    }),
    [
      submitWorkOrder,
      setWorkOrderAsPending,
      approveWorkOrder,
      releaseWorkOrder,
      rejectWorkOrder,
      cancelWorkOrder,
      denyCancelRequest,
      acceptCancelRequest,
      blockWorkOrder,
      transferWorkOrder,
      confirmTransfer,
      updateWorkOrder,
      deleteWorkOrder,
      saving,
      submitting,
      settingAsPending,
      approving,
      releasing,
      rejecting,
      cancelling,
      denyingCancellation,
      acceptingCancellation,
      blocking,
      transferring,
      confirmingTransfer,
      deleting,
    ],
  );
  return (
    <WorkOrdersContext value={value}>
      <WorkOrderModalProvider>
        <ActionBlockedModalBridge openRef={openActionBlockedModalRef} />
        {children}
      </WorkOrderModalProvider>
    </WorkOrdersContext>
  );
};

export const useWorkOrders = () => useGeneralContext(WorkOrdersContext, 'WorkOrders');
